import React from 'react'
import { Avatar, Button, Card, CardContent, CardHeader, Input } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import {
  addAnalysisEditor,
  listAnalysisEditors,
  removeAnalysisEditor,
  searchMasterUsers,
  type AnalysisEditorUser,
  type MasterProfileContact,
} from '../services/analysisEditorsService'

export default function AnalysisEditorsSettings() {
  const { add: toast } = useToast()
  const [loadingEditors, setLoadingEditors] = React.useState(true)
  const [editors, setEditors] = React.useState<AnalysisEditorUser[]>([])
  const [query, setQuery] = React.useState('')
  const [searching, setSearching] = React.useState(false)
  const [searchError, setSearchError] = React.useState<string | null>(null)
  const [searchResults, setSearchResults] = React.useState<MasterProfileContact[]>([])
  const [mutatingUserId, setMutatingUserId] = React.useState<string | null>(null)

  const editorIds = React.useMemo(() => new Set(editors.map((e) => e.id)), [editors])

  const loadEditors = React.useCallback(async () => {
    setLoadingEditors(true)
    try {
      const items = await listAnalysisEditors()
      setEditors(items)
    } catch (e: any) {
      toast({
        title: 'Permissão Editor (Análises)',
        description: e?.message || 'Falha ao carregar a lista de editores',
        variant: 'error',
      })
    } finally {
      setLoadingEditors(false)
    }
  }, [toast])

  React.useEffect(() => {
    loadEditors()
  }, [loadEditors])

  React.useEffect(() => {
    const q = query.trim()
    if (!q) {
      setSearching(false)
      setSearchError(null)
      setSearchResults([])
      return
    }

    let active = true
    setSearching(true)
    setSearchError(null)

    const t = setTimeout(async () => {
      try {
        const results = await searchMasterUsers({ query: q, limit: 8 })
        if (!active) return
        setSearchResults(results)
      } catch (e: any) {
        if (!active) return
        setSearchError(e?.message || 'Falha ao buscar usuários')
        setSearchResults([])
      } finally {
        if (active) setSearching(false)
      }
    }, 350)

    return () => {
      active = false
      clearTimeout(t)
    }
  }, [query])

  async function handleAdd(userId: string) {
    if (mutatingUserId) return
    setMutatingUserId(userId)
    try {
      await addAnalysisEditor(userId)
      toast({
        title: 'Permissão Editor (Análises)',
        description: 'Usuário adicionado como Editor com sucesso.',
        variant: 'success',
        soft: true,
      })
      await loadEditors()
    } catch (e: any) {
      toast({
        title: 'Permissão Editor (Análises)',
        description: e?.message || 'Falha ao adicionar Editor',
        variant: 'error',
      })
    } finally {
      setMutatingUserId(null)
    }
  }

  async function handleRemove(userId: string) {
    if (mutatingUserId) return
    setMutatingUserId(userId)
    try {
      await removeAnalysisEditor(userId)
      toast({
        title: 'Permissão Editor (Análises)',
        description: 'Permissão removida com sucesso.',
        variant: 'success',
        soft: true,
      })
      setEditors((prev) => prev.filter((e) => e.id !== userId))
    } catch (e: any) {
      toast({
        title: 'Permissão Editor (Análises)',
        description: e?.message || 'Falha ao remover Editor',
        variant: 'error',
      })
    } finally {
      setMutatingUserId(null)
    }
  }

  return (
    <Card>
      <CardHeader className="text-base font-semibold">Permissão Editor (postagens)</CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <div className="text-sm font-medium text-foreground/80">O que o Editor pode fazer</div>
          <p className="text-xs text-foreground/60">
            Usuários cadastrados como <span className="font-medium">Editor</span> podem <span className="font-medium">adicionar</span>,{' '}
            <span className="font-medium">editar</span> e <span className="font-medium">excluir</span> postagens no feed do módulo de Análises
            Cripto. Somente usuários adicionados aqui terão esse recurso.
          </p>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium text-foreground/80">Adicionar Editor</div>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar usuário por nome ou e-mail..."
          />

          {searchError && (
            <div className="text-xs text-destructive">
              {searchError}
            </div>
          )}

          {query.trim().length > 0 && (
            <div className="mt-2 space-y-2">
              {searching && (
                <div className="text-xs text-foreground/60">
                  Buscando...
                </div>
              )}

              {!searching && !searchError && searchResults.length === 0 && (
                <div className="text-xs text-foreground/60">
                  Nenhum usuário encontrado.
                </div>
              )}

              {!searching && searchResults.map((u) => {
                const id = String(u.id)
                const isAlreadyEditor = editorIds.has(id)
                const isMaster = String(u.role || '').trim() === 'master'
                const disabled = !!mutatingUserId || isAlreadyEditor || isMaster

                return (
                  <div
                    key={id}
                    className="flex items-center justify-between gap-4 rounded-md border border-border/60 px-3 py-2"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <Avatar
                        src={u.avatar_url || undefined}
                        alt={String(u.full_name || '').trim() || 'Usuário'}
                        className="h-7 w-7"
                        fallback={(String(u.full_name || '').trim()?.[0] || 'U')}
                      />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">
                          {String(u.full_name || '').trim() || '(Sem nome)'}
                        </div>
                        <div className="truncate text-xs text-foreground/60">
                          {String(u.email || '').trim() || '—'}
                          {u.role ? ` • ${u.role}` : ''}
                        </div>
                      </div>
                    </div>

                    <Button
                      type="button"
                      variant="outline"
                      disabled={disabled}
                      onClick={() => handleAdd(id)}
                    >
                      {isMaster ? 'Master (já possui)' : isAlreadyEditor ? 'Já é editor' : 'Adicionar'}
                    </Button>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium text-foreground/80">Editores cadastrados</div>

          {loadingEditors && (
            <div className="text-xs text-foreground/60">
              Carregando...
            </div>
          )}

          {!loadingEditors && editors.length === 0 && (
            <div className="text-xs text-foreground/60">
              Nenhum Editor cadastrado ainda.
            </div>
          )}

          {!loadingEditors && editors.length > 0 && (
            <div className="space-y-2">
              {editors.map((e) => (
                <div
                  key={e.id}
                  className="flex items-center justify-between gap-4 rounded-md border border-border/60 px-3 py-2"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar
                      src={e.avatarUrl || undefined}
                      alt={e.fullName || 'Usuário'}
                      className="h-7 w-7"
                      fallback={(e.fullName?.[0] || 'U')}
                    />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">
                        {e.fullName || '(Sem nome)'}
                      </div>
                      <div className="truncate text-xs text-foreground/60">
                        {e.email || '—'}
                        {e.role ? ` • ${e.role}` : ''}
                      </div>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    disabled={!!mutatingUserId}
                    onClick={() => handleRemove(e.id)}
                  >
                    Remover
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

